/**
 * スワイプクリップのタイムライン生成 (§5.3)。パラメータ → 停止位置と区間 (整数フレーム) → 各フレームのスクロール位置 s。
 * image-generator の docs/features/swipe-motion-clip-plan.md。
 *
 * **正本はここ (video-editor)**。image-generator の frontend/src/features/swipeClip/timeline.ts は
 * scripts/sync-swipe-clip.mjs による無改変コピー。backend/services/swipe_clip/params.py は
 * 停止位置と durationInFrames の計算 (A〜D) だけを同じ規則で持ち、fixtures/timeline.json で照合する。
 *
 * 依存なしの純関数。座標は**仮想画像 (切り詰め + 縮小後) の高さに対する %** (geometry.ts が保存形から変換する)。
 *
 * ## フレームの規則
 * - `frames(sec) = floor(sec × fps + 0.5)`。0 フレームの区間は捨てる
 * - 区間の境界フレームは次の区間の先頭に属する
 * - swipe 区間 (n フレーム) の i 番目 (0 始まり) の進行は `t = i / (n − 1)` (n = 1 なら t = 1)。
 *   先頭フレームは始点・最後のフレームは終点にぴったり着くので、前後の hold が 0 フレームでも
 *   evaluate(0) = stops[0]・最終フレーム = stops[count] が成り立つ
 */
import { SWIPE_BOUNCE_C, SWIPE_FPS, SWIPE_MAX_TOTAL_SEC, SWIPE_MIN_TOTAL_SEC, SWIPE_SEC_MIN, type SwipeFeel, type SwipeSwipes, type SwipeTiming } from './schema';

export interface SwipeTimelineInput {
  /** 仮想画像基準の開始位置 (画面上端) %。 */
  s0: number;
  /** 仮想画像基準の終了地点 (画面下端) %。 */
  sEndEdge: number;
  /** 画面に見える高さ (仮想画像の高さに対する %)。 */
  viewportPct: number;
  swipes: SwipeSwipes;
  timing: SwipeTiming;
  fps?: number;
}

export type SwipeSegmentKind = 'hold' | 'swipe';

export interface SwipeSegment {
  kind: SwipeSegmentKind;
  /** 区間の先頭フレーム (タイムライン上)。 */
  start: number;
  frames: number;
  from: number;
  to: number;
}

export interface SwipeTimeline {
  fps: number;
  durationInFrames: number;
  /** 静止ケース (A) か。 */
  isStatic: boolean;
  /** 仮想画像が画面より縦に短く contain で置くか (A の一種)。 */
  isContain: boolean;
  /** 停止位置 (count + 1 個。静止ケースは 1 個)。 */
  stops: number[];
  segments: SwipeSegment[];
  /** 最後の停止位置が終了地点 (up は開始地点) に届いたか。 */
  reachedEnd: boolean;
  /** 指定量が大きく途中で張り付いたとき、動かなくなる最初のスワイプ番号 (1 始まり)。無ければ null。 */
  stuckFromSwipe: number | null;
  /** フレーム番号 → スクロール位置 s (%)。 */
  evaluate: (frame: number) => number;
}

export type SwipeTimelineErrorCode = 'TOO_SHORT' | 'TOO_LONG' | 'SCENE_TOO_SHORT' | 'SCENE_TOO_LONG' | 'TOTAL_SEC_REQUIRED';

export class SwipeTimelineError extends Error {
  readonly code: SwipeTimelineErrorCode;
  /** SCENE_TOO_SHORT のときの最短秒数。 */
  readonly minSec?: number;
  constructor(code: SwipeTimelineErrorCode, message: string, minSec?: number) {
    super(message);
    this.name = 'SwipeTimelineError';
    this.code = code;
    this.minSec = minSec;
  }
}

export function frames(sec: number, fps: number = SWIPE_FPS): number {
  return Math.floor(sec * fps + 0.5);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** 「すっと止まる」: ease-out。 */
export function easeSettle(t: number): number {
  const u = clamp(t, 0, 1);
  return 1 - (1 - u) * (1 - u);
}

/** 「なめらか」: ease-in-out (3 次)。ゆっくり動き出してゆっくり止まる。 */
export function easeSmooth(t: number): number {
  const u = clamp(t, 0, 1);
  return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
}

/** 「一定速度」: linear。回数 1 回 + 長めの秒数で、途切れない自動スクロールになる。 */
export function easeLinear(t: number): number {
  return clamp(t, 0, 1);
}

/** 手触り → イージング関数。 */
export function easingForFeel(feel: SwipeFeel): (t: number) => number {
  switch (feel) {
    case 'bounce':
      return easeBounce;
    case 'smooth':
      return easeSmooth;
    case 'linear':
      return easeLinear;
    case 'settle':
    default:
      return easeSettle;
  }
}

/** 「少し戻る」: ease-out-back。e(0)=0, e(1)=1, 途中で e > 1 (行き過ぎ)。 */
export function easeBounce(t: number, c: number = SWIPE_BOUNCE_C): number {
  const u = clamp(t, 0, 1);
  const d = u - 1;
  return 1 + (c + 1) * d * d * d + c * d * d;
}

interface Budget {
  holdStartF: number;
  holdEndF: number;
  swipeF: number;
  gapF: number;
}

/**
 * explicit: 各区間を秒から整数フレームへ。fitScene: 目標フレーム数を先に確定し、最初 / 最後の静止は秒数どおり、
 * 残りをスワイプの長さと間の静止で入力した比のまま整数配分する (§5.3-D)。
 */
function buildBudget(input: SwipeTimelineInput, count: number, fps: number): Budget {
  const { timing } = input;
  const gaps = Math.max(count - 1, 0);
  const swipeMinF = frames(SWIPE_SEC_MIN, fps);
  const minTotalF = frames(SWIPE_MIN_TOTAL_SEC, fps);
  const maxTotalF = frames(SWIPE_MAX_TOTAL_SEC, fps);

  if (timing.mode === 'explicit') {
    const budget: Budget = {
      holdStartF: frames(timing.holdStartSec, fps),
      holdEndF: frames(timing.holdEndSec, fps),
      swipeF: count > 0 ? frames(timing.swipeSec, fps) : 0,
      gapF: gaps > 0 ? frames(timing.gapSec, fps) : 0,
    };
    const total = budget.holdStartF + budget.holdEndF + count * budget.swipeF + gaps * budget.gapF;
    if (total < minTotalF) {
      throw new SwipeTimelineError('TOO_SHORT', `合計が最短 ${SWIPE_MIN_TOTAL_SEC} 秒に満たないため作れません`);
    }
    if (total > maxTotalF) {
      throw new SwipeTimelineError('TOO_LONG', `合計が上限 ${SWIPE_MAX_TOTAL_SEC} 秒を超えるため作れません`);
    }
    return budget;
  }

  if (timing.totalSec == null) {
    throw new SwipeTimelineError('TOTAL_SEC_REQUIRED', 'シーンの尺 (totalSec) が必要です');
  }
  const target = frames(timing.totalSec, fps);
  if (target < minTotalF) {
    throw new SwipeTimelineError('SCENE_TOO_SHORT', `シーンの尺が短すぎます (最短 ${SWIPE_MIN_TOTAL_SEC} 秒)`, SWIPE_MIN_TOTAL_SEC);
  }
  if (target > maxTotalF) {
    throw new SwipeTimelineError('SCENE_TOO_LONG', `シーンの尺が上限 ${SWIPE_MAX_TOTAL_SEC} 秒を超えています`);
  }

  // fitScene: 最初 / 最後の静止は入力した秒数どおり。残りをスワイプの長さと間の静止で、入力した比を保って分け合う
  const holdStartF = frames(timing.holdStartSec, fps);
  const holdEndInputF = frames(timing.holdEndSec, fps);
  const minSec = timing.holdStartSec + timing.holdEndSec + count * SWIPE_SEC_MIN;

  // 静止ケース: 最初の静止は秒数どおり、残りは最後の静止
  if (count === 0) {
    const start = Math.min(holdStartF, target);
    return { holdStartF: start, holdEndF: target - start, swipeF: 0, gapF: 0 };
  }

  const rest = target - holdStartF - holdEndInputF;
  if (rest < count * swipeMinF) {
    throw new SwipeTimelineError('SCENE_TOO_SHORT', `シーンの尺が短すぎます (最短 ${round2(minSec)} 秒)`, round2(minSec));
  }
  const totalWeight = count * timing.swipeSec + gaps * timing.gapSec;
  let swipeF = totalWeight > 0 ? Math.floor((rest * timing.swipeSec) / totalWeight + 0.5) : Math.floor(rest / count);
  let gapF = gaps > 0 ? Math.floor((rest - count * swipeF) / gaps) : 0;
  if (swipeF < swipeMinF || gapF < 0) {
    // 比率どおりだとスワイプが短くなりすぎる: 間の静止を 0 にしてスワイプへ回す
    gapF = 0;
    swipeF = Math.floor(rest / count);
  }
  // 丸めの余り (gaps 未満のフレーム) は最後の静止へ
  const holdEndF = target - holdStartF - count * swipeF - gaps * gapF;
  return { holdStartF, holdEndF, swipeF, gapF };
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** 停止位置 (§5.3-B)。方向別に計算し、範囲内へクランプする。 */
export function buildSwipeStops(input: SwipeTimelineInput): { stops: number[]; sEnd: number; reachedEnd: boolean; stuckFromSwipe: number | null } {
  const { s0, sEndEdge, viewportPct, swipes } = input;
  const count = swipes.count;
  const sEnd = sEndEdge - viewportPct;
  const dist = sEnd - s0;
  const step = swipes.amountPct != null ? (viewportPct * swipes.amountPct) / 100 : dist / count;
  const stops: number[] = [];
  for (let i = 0; i <= count; i += 1) {
    stops.push(swipes.direction === 'down' ? Math.min(sEnd, s0 + step * i) : Math.max(s0, sEnd - step * i));
  }
  const last = stops[count] ?? s0;
  const goal = swipes.direction === 'down' ? sEnd : s0;
  const reachedEnd = Math.abs(last - goal) < 1e-9;
  let stuckFromSwipe: number | null = null;
  for (let i = 1; i <= count; i += 1) {
    if (Math.abs((stops[i] ?? 0) - (stops[i - 1] ?? 0)) < 1e-9) {
      stuckFromSwipe = i;
      break;
    }
  }
  return { stops, sEnd, reachedEnd, stuckFromSwipe };
}

/** 静止ケース (§5.3-A) の判定。 */
export function isStaticSwipe(input: SwipeTimelineInput): { isStatic: boolean; isContain: boolean } {
  const isContain = input.viewportPct >= 100;
  const noRoom = input.sEndEdge - input.s0 <= input.viewportPct;
  return { isStatic: isContain || noRoom || input.swipes.count === 0, isContain };
}

export function buildSwipeTimeline(input: SwipeTimelineInput): SwipeTimeline {
  const fps = input.fps ?? SWIPE_FPS;
  const { isStatic, isContain } = isStaticSwipe(input);

  const segments: SwipeSegment[] = [];
  let cursor = 0;
  const push = (kind: SwipeSegmentKind, n: number, from: number, to: number) => {
    if (n <= 0) return;
    segments.push({ kind, start: cursor, frames: n, from, to });
    cursor += n;
  };

  let stops: number[];
  let reachedEnd = true;
  let stuckFromSwipe: number | null = null;

  if (isStatic) {
    const s = clamp(input.s0, 0, Math.max(0, 100 - input.viewportPct));
    stops = [s];
    const budget = buildBudget(input, 0, fps);
    push('hold', budget.holdStartF, s, s);
    push('hold', budget.holdEndF, s, s);
  } else {
    const built = buildSwipeStops(input);
    stops = built.stops;
    reachedEnd = built.reachedEnd;
    stuckFromSwipe = built.stuckFromSwipe;
    const count = input.swipes.count;
    const budget = buildBudget(input, count, fps);
    push('hold', budget.holdStartF, stops[0] ?? 0, stops[0] ?? 0);
    for (let i = 1; i <= count; i += 1) {
      const from = stops[i - 1] ?? 0;
      const to = stops[i] ?? 0;
      push('swipe', budget.swipeF, from, to);
      if (i < count) push('hold', budget.gapF, to, to);
    }
    const last = stops[count] ?? 0;
    push('hold', budget.holdEndF, last, last);
  }

  const durationInFrames = cursor;
  const lo = Math.min(stops[0] ?? 0, stops[stops.length - 1] ?? 0);
  const hi = Math.max(stops[0] ?? 0, stops[stops.length - 1] ?? 0);
  const ease = easingForFeel(input.swipes.feel);

  const evaluate = (frame: number): number => {
    const f = clamp(Math.floor(frame), 0, Math.max(0, durationInFrames - 1));
    let seg: SwipeSegment | undefined;
    for (const s of segments) {
      if (f >= s.start && f < s.start + s.frames) {
        seg = s;
        break;
      }
    }
    if (!seg) return stops[stops.length - 1] ?? 0;
    if (seg.kind === 'hold') return seg.from;
    const t = seg.frames <= 1 ? 1 : (f - seg.start) / (seg.frames - 1);
    const value = seg.from + (seg.to - seg.from) * ease(t);
    return clamp(value, lo, hi);
  };

  return { fps, durationInFrames, isStatic, isContain, stops, segments, reachedEnd, stuckFromSwipe, evaluate };
}

/** 保存する実尺 (秒) = durationInFrames / fps (§5.3-C)。 */
export function swipeDurationSec(timeline: SwipeTimeline): number {
  return timeline.durationInFrames / timeline.fps;
}
